import { WMS } from '@/constants/featureFlags';
import { useClientUser } from '@/hooks';
import { enrichManifest } from '@/utilities/enrichManifest';
import { useMutation, useQuery } from '@apollo/client';
import { EXCEPTION_TYPES } from '@onward-delivery/core';
import { captureException } from '@sentry/react';
import React, { createContext, useEffect, useMemo, useState, useRef } from 'react';
import { DAYS_30, ORDER_SEARCHABLE, TABS } from './constants';
import {
    INSERT_MANIFEST,
    GET_MANIFESTS,
    GET_MANIFESTS_SUMMARY,
    GET_ORDERS,
    UPSERT_ITEMS,
    GET_EXTERNAL_MANIFESTS_SUMMARY,
    INSERT_MANIFEST_AND_UPSERT_ITEMS,
    DELETE_MANIFEST_BY_PK,
    DELETE_MANIFEST_INBOUND_ITEMS,
} from './graphql';
import { normalizeManifestListRow } from './normalize';
import {
    MANIFEST_INSERT_COLUMNS,
    buildManifestSearchFilter,
    buildOrderPickerRows,
    isOnlySingleShipperType,
} from './utils';
import { isEmpty, omit, pick } from 'lodash';
import { ITEM_READONLY_FIELDS } from '@onward-delivery/core';
import { OUTBOUND_MANIFEST_TYPES, RETURN_MANIFEST_TYPE, manifestTypeFilter } from '@/constants/manifestStatuses';
import { useTableColumns } from './modals/columns';
import { useUrlSearchTerm } from './useUrlSearchTerm';

export const Context = createContext();

// `modalOnly` mounts the store purely to drive the Create/Update Manifest dialog (e.g. from the
// single-manifest page's "+"): the list/summary queries are skipped so we don't refetch the whole
// Manifests page. The order-picker query (GET_ORDERS) still runs once a manifest type is chosen.
export const ContextProvider = ({ children, modalOnly = false }) => {
    const { user_id: client_id, locations: warehouses, circles, shipping_partners } = useClientUser();
    const [tab, setTab] = useState(TABS.INBOUND);
    const [newManifestDirection, setNewManifestDirection] = useState(null);
    const [newManifest, setNewManifest] = useState(null);
    const [showCreateManifestModal, setShowCreateManifestModal] = useState(false);
    const [searchManifests, setSearchManifests] = useUrlSearchTerm(!modalOnly);
    const effectiveSearch = searchManifests?.trim();
    const [searchOrders, setSearchOrders] = useState('');
    const [filters, setFilters] = useState(tab?.defaultFilters || {});
    const [notification, setNotification] = useState({});
    const [hasExternal, setHasExternal] = useState(false);
    const [selectedRows, selectRows] = useState([]);
    const [itemQuantities, setItemQuantities] = useState({});
    // Bumped when we seed the picker maps directly (update mode) so the item/order map memos recompute.
    const [mapEpoch, setMapEpoch] = useState(0);
    const [newManifestType, setNewManifestType] = useState();

    const [manifestCutoff, orderCutoff] = useMemo(() => {
        return [new Date(Date.now() - DAYS_30).toISOString(), new Date(Date.now() - DAYS_30 * 12).toISOString()];
    }, []);

    const ordersMapRef = useRef({});

    const manifestFilters = useMemo(() => {
        const combinedFilters = [];

        // RETURN is a display type over INBOUND, so "All" only needs the real types.
        const baseFilter = filters.type
            ? manifestTypeFilter(filters.type)
            : { type: { _in: tab.typeOptions.filter((type) => type !== RETURN_MANIFEST_TYPE) } };
        combinedFilters.push(baseFilter);

        const searchFilter = buildManifestSearchFilter(effectiveSearch);
        if (searchFilter) {
            combinedFilters.push(searchFilter);
        }

        return combinedFilters;
    }, [tab, effectiveSearch, filters.type, filters.shipperSelect]);

    const orderFilters = useMemo(() => {
        if (!newManifest?.type) return null;

        let orderFilters = [];

        if (!circles?.[WMS]) {
            orderFilters.push(...[{ oms: { _eq: false } }, { carrier_id: { _eq: client_id } }]);
        }

        if (searchOrders) {
            orderFilters.push({
                _or: [
                    ...ORDER_SEARCHABLE.map((attr) => {
                        if (attr.includes('.')) {
                            const parts = attr.split('.');
                            return parts.reduceRight((acc, part, index) => {
                                if (index === parts.length - 1) {
                                    return { [part]: { _ilike: `%${searchOrders.trim()}%` } };
                                }
                                return { [part]: acc };
                            }, {});
                        }
                        return { [attr]: { _ilike: `%${searchOrders.trim()}%` } };
                    }),
                ],
            });
        }

        switch (newManifest.type) {
            case 'INBOUND':
                orderFilters.push(
                    ...[
                        {
                            _or: [
                                {
                                    itemsByOrderId: {
                                        _not: { manifests: { manifest: { type: { _in: ['INBOUND', 'CROSS_DOCK'] } } } },
                                    },
                                },
                                {
                                    itemsByOrderId: {
                                        exceptions: {
                                            exception: {
                                                reported_at: { _eq: 'PICKUP' },
                                                type: {
                                                    _in: [
                                                        EXCEPTION_TYPES.OVERAGE,
                                                        EXCEPTION_TYPES.SHORTAGE,
                                                        EXCEPTION_TYPES.DAMAGED,
                                                    ],
                                                },
                                            },
                                        },
                                    },
                                },
                            ],
                        },
                        {
                            _not: { routes: { type: { _eq: 'PICKUP' } } },
                        },
                        { forward_stocked_status: { _is_null: true } },
                    ]
                );
                break;
            case 'CROSS_DOCK':
                orderFilters.push(
                    ...[
                        {
                            itemsByOrderId: {
                                _not: { manifests: { manifest: { type: { _in: ['INBOUND', 'CROSS_DOCK'] } } } },
                            },
                        },
                        { _not: { routes: { type: { _eq: 'PICKUP' } } } },
                        { wh_events: { action: { _ilike: `%:ADD_CD` } } },
                        { forward_stocked_status: { _is_null: true } },
                    ]
                );
                break;
            case 'OUTBOUND':
                orderFilters.push(
                    ...[
                        {
                            _not: { routes: {} },
                        },
                        {
                            itemsByOrderId: { _not: { manifests: { manifest: { type: { _eq: 'OUTBOUND' } } } } },
                        },
                        {
                            order_type: { _nin: ['will_call', 'wh_inventory'] },
                        },
                    ]
                );
                break;
            case 'RETURN_TO_SENDER':
                orderFilters.push(
                    ...[
                        {
                            itemsByOrderId: {
                                _not: { manifests: { manifest: { type: { _eq: 'RETURN_TO_SENDER' } } } },
                            },
                        },
                        {
                            itemsByOrderId: {
                                exceptions: {
                                    exception: {
                                        reported_at: { _eq: 'PICKUP' },
                                        type: {
                                            _in: [
                                                EXCEPTION_TYPES.OVERAGE,
                                                EXCEPTION_TYPES.SHORTAGE,
                                                EXCEPTION_TYPES.DAMAGED,
                                            ],
                                        },
                                    },
                                },
                            },
                        },
                    ]
                );
                break;
            case 'WILL_CALL':
                orderFilters.push(
                    ...[
                        { itemsByOrderId: { _not: { manifests: { manifest: { type: { _eq: 'WILL_CALL' } } } } } },
                        { order_type: { _eq: 'will_call' } },
                        ...(newManifest?.isDraft
                            ? [
                                  { shipper_id: { _eq: newManifest.partner_client_id } },
                                  {
                                      itemsByOrderId: {
                                          _or: (newManifest.draft_items || []).map((i) => ({
                                              _or: [{ sku: { _ilike: i.sku } }],
                                          })),
                                      },
                                  },
                              ]
                            : []),
                    ]
                );
                break;
        }

        return orderFilters;
    }, [circles, client_id, searchOrders, newManifest]);

    const { data, loading: queryLoading } = useQuery(GET_MANIFESTS_SUMMARY, {
        variables: {
            client_id,
            cutoff: effectiveSearch ? '2020-01-01T12:00:00+00:00' : manifestCutoff,
            filters: manifestFilters,
        },
        skip: modalOnly,
        fetchPolicy: 'cache-and-network',
        onError: (err) => {
            captureException(err);
            console.log(err);
        },
    });

    const { data: draftData, loading: draftLoading } = useQuery(GET_MANIFESTS, {
        variables: {
            client_id,
            cutoff: effectiveSearch ? '2020-01-01T12:00:00+00:00' : manifestCutoff,
            filters: [{ type: { _in: 'WILL_CALL' } }, ...manifestFilters.filter((f) => !f.type)],
        },
        skip: modalOnly,
        onError: (err) => {
            captureException(err);
            console.log(err);
        },
    });

    const { data: externalData, loading: externalQueryLoading } = useQuery(GET_EXTERNAL_MANIFESTS_SUMMARY, {
        variables: {
            client_id,
            cutoff: manifestCutoff,
            filters: manifestFilters,
        },
        skip: modalOnly,
        fetchPolicy: 'cache-and-network',
        onCompleted: (data) => {
            setHasExternal(data.manifests.length > 0);
        },
    });

    const manifests = useMemo(() => {
        const allManifests = (data?.manifests || [])
            .map((m) => normalizeManifestListRow(m))
            .filter((manifest) => isEmpty(manifest.draft_items));
        if (filters.status === 'ACTIVE') {
            return allManifests.filter((manifest) => tab.activeStatuses?.includes(manifest.status));
        }
        return allManifests.filter((manifest) => !filters.status || manifest.status === filters.status);
    }, [data, filters.status]);

    const externalManifests = useMemo(() => {
        const allManifests = (externalData?.manifests || [])
            .map((m) => normalizeManifestListRow(m))
            .filter((manifest) => isEmpty(manifest.draft_items));
        return allManifests.filter((manifest) => !filters.status || manifest.status === filters.status);
    }, [externalData, filters.status]);

    const draftManifests = useMemo(() => {
        const allManifests = (draftData?.manifests || [])
            .map((m) => {
                return { ...enrichManifest(m), isDraft: true };
            })
            .filter((manifest) => !isEmpty(manifest.draft_items));
        return allManifests;
    }, [draftData]);

    const {
        data: ordersData,
        previousData: prevOrdersData,
        loading: ordersLoading,
        refetch: refetchOrders,
    } = useQuery(GET_ORDERS, {
        variables: {
            client_id,
            cutoff: searchOrders ? '2020-01-01T12:00:00+00:00' : orderCutoff,
            filters: orderFilters,
        },
        skip: !orderFilters,
        onError: (err) => {
            captureException(err);
            console.error(err);
            setNotification({ severity: 'error', message: 'Error loading orders' });
        },
    });

    // Fall back to the previous result while a new search is in flight
    const ordersFetchedForTypeRef = useRef(null);
    const orders = useMemo(() => {
        if (ordersData) {
            ordersFetchedForTypeRef.current = newManifest?.type;
            return ordersData.orders || [];
        }
        if (prevOrdersData && ordersFetchedForTypeRef.current === newManifest?.type) {
            return prevOrdersData.orders || [];
        }
        return [];
    }, [ordersData, prevOrdersData, newManifest?.type]);

    const shipperOptions = useMemo(() => {
        if (tab.value === 'OUTBOUND') {
            return [
                ...new Set(
                    manifests.reduce((acc, m) => {
                        const orderShippers = (m?.route?.orders || []).reduce((orderAcc, o) => {
                            const shipperName = o?.order?.order_shipper?.business_name;
                            if (shipperName) {
                                orderAcc.push(shipperName);
                            }
                            return orderAcc;
                        }, []);
                        return [...acc, ...orderShippers];
                    }, [])
                ),
            ];
        }

        return [
            ...new Set(
                manifests.reduce((acc, m) => {
                    const itemShippers = (m?.items || []).reduce((itemAcc, i) => {
                        const shipperName = i?.item?.order?.order_shipper?.business_name;
                        if (shipperName) {
                            itemAcc.push(shipperName);
                        }
                        return itemAcc;
                    }, []);
                    return [...acc, ...itemShippers];
                }, [])
            ),
        ];
    }, [manifests, tab]);

    const [insertManifestAndUpsertItems, { loading: createLoading }] = useMutation(INSERT_MANIFEST_AND_UPSERT_ITEMS, {
        refetchQueries: [
            {
                query: GET_MANIFESTS_SUMMARY,
                variables: {
                    client_id,
                    cutoff: effectiveSearch ? '2020-01-01T12:00:00+00:00' : manifestCutoff,
                    filters: manifestFilters,
                },
            },
        ],
        awaitRefetchQueries: false,
        onError: (error) => {
            console.error(error);
            captureException(error);
            setNotification({ severity: 'error', message: 'Error creating manifest' });
            setItemQuantities({});
        },
        onCompleted: ({ created }) => {
            setNotification({
                severity: 'success',
                message: `Manifest created - ${created.returning[0].manifest_number}`,
            });
            setItemQuantities({});
            refetchOrders();
        },
    });

    const [upsertItems, { loading: splitLoading }] = useMutation(UPSERT_ITEMS, {
        onError: (error) => {
            console.error(error);
            captureException(error);
            setNotification({ severity: 'error', message: 'Error splitting items' });
        },
        onCompleted: ({ created }) => {
            refetchOrders();
        },
    });

    const [deleteDraftManifest] = useMutation(DELETE_MANIFEST_BY_PK, {
        onError: (error) => {
            console.error(error);
            captureException(error);
            setNotification({ severity: 'error', message: 'Error deleting manifest' });
        },
        update: (cache, { data: { delete_manifests_by_pk } }) => {
            cache.updateQuery(
                {
                    query: GET_MANIFESTS,
                    variables: {
                        client_id,
                        cutoff: effectiveSearch ? '2020-01-01T12:00:00+00:00' : manifestCutoff,
                        filters: [{ type: { _in: 'WILL_CALL' } }, ...manifestFilters.filter((f) => !f.type)],
                    },
                },
                (data) => {
                    return {
                        ...data,
                        manifests:
                            data?.manifests?.filter(
                                (manifest) => manifest.manifest_id !== delete_manifests_by_pk.manifest_id
                            ) || [],
                    };
                }
            );
        },
        onCompleted: () => {
            // Full reset — a partial one leaks search/selection state into the next create
            closeCreateManifest();
            refetchOrders();
        },
    });

    const [deleteManifestInboundItems] = useMutation(DELETE_MANIFEST_INBOUND_ITEMS, {
        onError: (error) => {
            console.error(error);
            captureException(error);
            setNotification({ severity: 'error', message: 'Error removing items from manifest' });
        },
    });

    const createManifest = () => {
        const {
            items: selectedItems,
            includedShippers,
            share_manifest,
            isDraft,
            isUpdate,
            originalItemIds = [],
        } = newManifest || {};
        const manifestColumns = pick(newManifest || {}, MANIFEST_INSERT_COLUMNS);

        // Resolve the type once so the single-shipper check and the insert payload agree.
        const resolvedType = isDraft ? 'WILL_CALL' : manifestColumns.type || newManifestType || tab.value;

        if (isOnlySingleShipperType(resolvedType)) {
            // Defensive — the shipper-first UI should already guarantee exactly one shipper.
            if ((includedShippers || []).length !== 1) {
                setNotification({
                    severity: 'error',
                    message:
                        'An inbound manifest can only contain orders from a single shipper — remove items from other shippers.',
                });
                return Promise.resolve({});
            }
            manifestColumns.partner_client_id = includedShippers[0];
        }

        const selectedIds = Object.entries(selectedItems || {})
            .filter(([_, selected]) => selected)
            .map(([item_id]) => item_id);
        // Create: insert every selected item. Update: only newly-added items become new mappings
        // (existing ones stay; deselected ones are deleted after the upsert).
        const addIds = isUpdate ? selectedIds.filter((id) => !originalItemIds.includes(id)) : selectedIds;
        const items = addIds.map((item_id) => ({ item_id }));

        const shareManifest =
            share_manifest ||
            (includedShippers || []).some((shipperId) =>
                shipping_partners.some((partner) => partner.shipper_id === shipperId && partner.share_manifests)
            );

        const itemsToSplit = items.filter((i) => {
            return itemQuantities?.[i.item_id] && itemQuantities[i.item_id] < itemsMap?.[i.item_id]?.quantity;
        });

        const splitItemInserts = [];
        let existingWhId = manifestColumns.warehouse_id;

        itemsToSplit.forEach((i) => {
            const splitAmount = itemQuantities[i.item_id];
            const itemObject = itemsMap[i.item_id];
            const existingInboundManifestId = itemObject.manifests[0]?.manifest_id;
            const palletInfoToCopy = omit(itemObject.pallet, [
                'pallet_id',
                'pallet_number',
                '__typename',
                'warehouse_location',
                'logs',
            ]);

            if (isDraft) {
                existingWhId = itemObject.manifests[0]?.manifest?.warehouse_id || existingWhId;
            }

            const { item_id, quantity, pallet_id, order, order_number, ...itemFields } = omit(
                itemObject,
                ITEM_READONLY_FIELDS
            );
            const item1 = { ...itemFields, quantity: splitAmount, item_id: item_id, pallet_id: pallet_id };
            const item2 = {
                ...itemFields,
                quantity: quantity - splitAmount,
                //If creating will-call manifest split onto new pallet for tracking picking/staging separate from original.
                ...(newManifest.type === 'WILL_CALL'
                    ? {
                          pallet: itemObject.pallet
                              ? {
                                    data: palletInfoToCopy,
                                    on_conflict: {
                                        constraint: 'pallet_items_pkey',
                                        update_columns: [
                                            'pallet_name',
                                            'type',
                                            'warehouse_id',
                                            'warehouse_status',
                                            'is_pool',
                                        ],
                                    },
                                }
                              : {},
                          // Only link the remainder back when the item has a prior manifest —
                          // a null manifest_id mapping fails the whole insert.
                          ...(existingInboundManifestId
                              ? {
                                    manifests: {
                                        data: { manifest_id: existingInboundManifestId },
                                        on_conflict: {
                                            constraint: 'manifests_inbound_items_pkey',
                                            update_columns: ['item_id', 'manifest_id'],
                                        },
                                    },
                                }
                              : {}),
                      }
                    : {}),
            };
            splitItemInserts.push(item1);
            splitItemInserts.push(item2);
        });

        const insertPromise = insertManifestAndUpsertItems({
            variables: {
                manifests: [
                    {
                        ...manifestColumns,
                        client_id,
                        type: resolvedType,
                        source: manifestColumns.source || 'MANUAL',
                        items: {
                            data: items,
                        },
                        share_manifest: shareManifest,
                        draft_items: [],
                        warehouse_id: existingWhId,
                    },
                ],
                items: splitItemInserts,
            },
        });

        if (isUpdate) {
            // Deselected items lose their mapping (delete permitted per the manifests_inbound_items
            // user delete permission). Return the insert result so callers see the manifest row.
            const removedIds = originalItemIds.filter((id) => !selectedIds.includes(id));
            return Promise.all([
                insertPromise,
                ...(removedIds.length
                    ? [
                          deleteManifestInboundItems({
                              variables: { manifest_id: manifestColumns.manifest_id, item_ids: removedIds },
                          }),
                      ]
                    : []),
            ]).then(([insertResult]) => insertResult);
        }

        return insertPromise;
    };

    // Partnered-shipper auto-share: when armed, the Share field is forced to Yes and disabled.
    const autoShareArmed = useMemo(() => {
        return (newManifest?.includedShippers || []).some((shipperId) =>
            (shipping_partners || []).some((partner) => partner.shipper_id === shipperId && partner.share_manifests)
        );
    }, [newManifest?.includedShippers, shipping_partners]);

    const defaultWarehouseId = () =>
        ((warehouses || []).find((location) => location.preferred_inbound) || warehouses?.[0])?.location_id;

    // Changing type swaps the eligibility filters entirely — clear staged and committed picks.
    // Create-only: type is immutable once the manifest exists (the Type field is disabled on update),
    // because clearing `items` here while `originalItemIds` survives would delete every existing mapping.
    const changeManifestType = (type) => {
        setNewManifest((prev) => ({ ...prev, type, items: {}, includedShippers: [] }));
        setNewManifestType(type);
        selectRows([]);
    };

    const openCreateManifest = (direction) => {
        setNewManifestDirection(direction);
        setNewManifest({
            warehouse_id: defaultWarehouseId(),
            receiving_date: new Date().toISOString(),
        });
        setShowCreateManifestModal(true);
    };

    // Will-call draft requests skip the choice dialogs; seed the same defaults as the
    // normal flow for anything the shipper's request didn't carry.
    const openDraftManifest = (draftManifest) => {
        const { cubes, items, orders, received, staged, picked_up, total, __typename, ...rest } = draftManifest;
        setNewManifestDirection('OUTBOUND');
        setNewManifest({
            ...rest,
            share_manifest: true,
            receiving_date: rest.receiving_date || new Date().toISOString(),
            warehouse_id: rest.warehouse_id || defaultWarehouseId(),
        });
        setNewManifestType('WILL_CALL');
        setShowCreateManifestModal(true);
    };

    // Reopen the Create Manifest dialog to edit an existing manifest's membership (the single-manifest
    // page's "+"). Seeds the picker maps from the manifest's current orders/items (the candidate query
    // excludes already-manifested items, so they wouldn't appear otherwise), preselects them, and flags
    // the flow isUpdate so submit upserts by manifest_id + deletes deselected mappings.
    const openUpdateManifest = (manifest) => {
        const currentOrders = manifest?.orders || [];
        const seededItems = { ...itemsMapRef.current };
        const seededOrders = { ...ordersMapRef.current };
        const selected = {};

        currentOrders.forEach((order) => {
            // Seed ONLY the items actually on the manifest (manifestItems) — not the whole order's
            // itemsByOrderId, which would over-select unmanifested items and show their full quantities.
            const orderItems = (
                order.manifestItems && order.manifestItems.length
                    ? order.manifestItems.map((mi) => mi?.item || mi)
                    : order.itemsByOrderId || []
            ).filter(Boolean);
            const normalizedOrder = { ...order, itemsByOrderId: orderItems };
            seededOrders[order.order_id] = normalizedOrder;
            orderItems.forEach((item) => {
                if (item?.item_id) {
                    seededItems[item.item_id] = { ...item, order: normalizedOrder };
                    selected[item.item_id] = true;
                }
            });
        });

        itemsMapRef.current = seededItems;
        ordersMapRef.current = seededOrders;
        setMapEpoch((e) => e + 1);

        const direction = OUTBOUND_MANIFEST_TYPES.includes(manifest.type) ? 'OUTBOUND' : 'INBOUND';
        setNewManifestDirection(direction);
        setNewManifestType(manifest.type);
        // Single-shipper types lock the shipper-filter UI to the manifest's existing shipper so
        // adding orders can't introduce a second one. partner_client_id is left as-is on update
        // (the update mutation's on_conflict update_columns doesn't touch it anyway).
        const lockedShipperName = isOnlySingleShipperType(manifest.type)
            ? currentOrders[0]?.order_shipper?.business_name || null
            : null;
        setNewManifest({
            manifest_id: manifest.manifest_id,
            name: manifest.name,
            type: manifest.type,
            warehouse_id: manifest.warehouse_id,
            receiving_date: manifest.receiving_date || new Date().toISOString(),
            staging_zone: manifest.staging_zone,
            reference_id: manifest.reference_id,
            partner_client_id: manifest.partner_client_id,
            // Seed so an update can't silently un-share — share_manifest IS in the upsert's
            // update_columns, so an unseeded (undefined) value writes false over true.
            share_manifest: manifest.share_manifest,
            lockedShipperName,
            items: selected,
            // Seed from the manifest's existing items so the single-shipper guard (and autoShareArmed)
            // are correct even if the user updates without touching the order table.
            includedShippers: computeIncludedShippers(selected),
            originalItemIds: Object.keys(selected),
            isUpdate: true,
        });
        setShowCreateManifestModal(true);
    };

    const closeCreateManifest = () => {
        setNewManifest(null);
        setNewManifestDirection(null);
        setNewManifestType(undefined);
        selectRows([]);
        setSearchOrders('');
        setItemQuantities({});
        // Drop cross-search accumulations so long sessions don't grow unbounded.
        itemsMapRef.current = {};
        ordersMapRef.current = {};
        setShowCreateManifestModal(false);
    };

    // Direction chosen in the create flow, not the page tab. Receiving date rides along so the
    // Est. Received Date column can flag orders not due until after it.
    const CREATE_MANIFEST_COLUMNS = useTableColumns(
        TABS[newManifestDirection] || tab,
        newManifest?.receiving_date,
        newManifest?.type
    );

    const generatedData = useMemo(() => buildOrderPickerRows(orders), [orders]);

    // Memo so the context exposes a fresh object the same render the orders arrive
    const ordersMap = useMemo(() => {
        const next = { ...ordersMapRef.current };
        orders.forEach((order) => {
            if (!next[order.order_id]) {
                next[order.order_id] = order;
            }
        });
        ordersMapRef.current = next;
        return next;
    }, [orders, mapEpoch]);

    const itemsMapRef = useRef({});
    const itemsMap = useMemo(() => {
        const next = { ...itemsMapRef.current };
        orders.forEach((order) => {
            (order.itemsByOrderId || []).forEach((item) => {
                next[item.item_id] = { ...item, order };
            });
        });
        itemsMapRef.current = next;
        return next;
    }, [orders, mapEpoch]);

    // Pure: matched draft rows + the split qty to seed per matched item (seeding runs in the effect below).
    const { matchedDraftItems, draftQuantitySeed } = useMemo(() => {
        if (!newManifest?.draft_items) {
            return { matchedDraftItems: {}, draftQuantitySeed: {} };
        }

        const selectedItemIds = [
            ...new Set([
                ...Object.entries(newManifest.items || {})
                    .filter(([_, selected]) => selected)
                    .map(([itemId]) => itemId),
                ...selectedRows,
            ]),
        ];

        const matched = {};
        const seed = {};

        newManifest.draft_items.forEach((draftItem, index) => {
            const hasMatch = selectedItemIds.some((itemId) => {
                const item = itemsMap[itemId];
                if (!item) return false;
                const itemPalletId = item.pallet?.pallet_name || item.pallet?.pallet_id;
                const matches =
                    !!item.sku &&
                    (item.sku === draftItem.sku || item.sku.toUpperCase() === draftItem.sku) &&
                    (!draftItem.pallet_id || draftItem.pallet_id === itemPalletId);

                if (matches && seed[itemId] == null) {
                    seed[itemId] = draftItem.quantity;
                }

                return matches;
            });

            matched[index] = hasMatch;
        });

        return { matchedDraftItems: matched, draftQuantitySeed: seed };
    }, [newManifest, itemsMap, selectedRows]);

    useEffect(() => {
        const pending = Object.entries(draftQuantitySeed).filter(([itemId]) => !itemQuantities[itemId]);
        if (!pending.length) return;
        setItemQuantities((prev) => {
            const next = { ...prev };
            pending.forEach(([itemId, quantity]) => {
                if (!next[itemId]) next[itemId] = quantity;
            });
            return next;
        });
    }, [draftQuantitySeed, itemQuantities]);

    // A shipper is "included" when ANY of its items is on the manifest.
    // Used to auto-share manifests with partnered shippers on create.
    const computeIncludedShippers = (items) => {
        return Object.keys(items).reduce((acc, itemId) => {
            if (!items[itemId]) {
                return acc;
            }
            const shipperId = itemsMapRef.current[itemId]?.order?.shipper_id;
            if (shipperId && !acc.includes(shipperId)) {
                acc.push(shipperId);
            }
            return acc;
        }, []);
    };

    const addSelectedRowsToManifest = () => {
        setNewManifest((prev) => {
            const items = { ...(prev?.items || {}) };
            selectedRows.forEach((itemId) => {
                items[itemId] = true;
            });
            return { ...prev, items, includedShippers: computeIncludedShippers(items) };
        });
        selectRows([]);
    };

    const removeManifestItems = (itemIds) => {
        setNewManifest((prev) => {
            const items = { ...(prev?.items || {}) };
            itemIds.forEach((itemId) => {
                delete items[itemId];
            });
            return { ...prev, items, includedShippers: computeIncludedShippers(items) };
        });
    };

    return (
        <Context.Provider
            value={{
                state: {
                    tab,
                    newManifest,
                    newManifestDirection,
                    selectedRows,
                    autoShareArmed,
                    manifests,
                    externalManifests,
                    draftManifests,
                    searchManifests,
                    orders,
                    searchOrders,
                    notification,
                    filters,
                    warehouses,
                    hasExternal,
                    shipperOptions,
                    CREATE_MANIFEST_COLUMNS,
                    generatedData,
                    itemsMap,
                    ordersMap,
                    itemQuantities,
                    newManifestType,
                    showCreateManifestModal,
                    matchedDraftItems,
                },
                loading: {
                    queryLoading,
                    createLoading,
                    ordersLoading,
                    externalQueryLoading,
                    draftLoading,
                },
                callbacks: {
                    setTab,
                    setNewManifest,
                    setNewManifestDirection,
                    addSelectedRowsToManifest,
                    removeManifestItems,
                    changeManifestType,
                    openCreateManifest,
                    openDraftManifest,
                    openUpdateManifest,
                    closeCreateManifest,
                    setShowCreateManifestModal,
                    createManifest,
                    setSearchManifests,
                    setSearchOrders,
                    clearNotification: () => setNotification({}),
                    setFilters,
                    selectRows,
                    setItemQuantities,
                    setNewManifestType,
                    deleteDraftManifest,
                },
            }}
        >
            {children}
        </Context.Provider>
    );
};

export const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} />
        </ContextProvider>
    );
